import { Link } from 'react-router-dom';
import { ASSETS, PATHS, WEEKLY_SHAPE } from '../lib/content';
import { Circle } from 'lucide-react';

export default function Courses() {
  const path1 = PATHS[0];

  return (
    <div data-testid="page-courses">
      {/* HERO with image */}
      <section className="relative h-[44vh] min-h-[380px]">
        <img src={ASSETS.sunThroughTrees} alt="Soft morning light filtering through trees" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/40 via-brand-bg/20 to-brand-bg" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full text-center">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4 bg-brand-bone/80 inline-block px-4 py-1.5 rounded-full">The Course</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-brown leading-[1.05] max-w-3xl mx-auto">
              The Three Paths
            </h1>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-base md:text-lg text-brand-brown-light/90 leading-relaxed">
            Three 12-week online journeys held one after another across a year. Each Path is complete in itself, and each prepares the ground for the next — like seasons unfolding within a life.
          </p>
        </div>
      </section>

      {/* PATHS OVERVIEW */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {PATHS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`course-overview-${i + 1}`}
              className="bg-brand-bone rounded-3xl p-8 border border-brand-brown/5"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold">{p.label}</span>
                <span className="font-sans text-xs text-brand-brown-muted">12 weeks</span>
              </div>
              <h3 className="font-serif text-2xl md:text-[1.6rem] text-brand-brown leading-snug mb-4">{p.title}</h3>
              <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{p.description}</p>
              <div className="mt-6 flex flex-wrap gap-1.5">
                {p.themes.slice(0, 4).map((t) => (
                  <span key={t} className="text-[11px] font-sans px-2.5 py-1 rounded-full bg-brand-sage text-brand-brown-light">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PATH 1 DETAILED */}
      <section className="py-24 md:py-28 bg-brand-bg-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div className="relative md:sticky md:top-28">
              <div className="absolute -inset-3 bg-brand-peach/40 rounded-3xl -rotate-1" aria-hidden />
              <img src={ASSETS.heroMeadow} alt="A gentle nature path winding through wildflowers at sunset" className="relative rounded-3xl object-cover w-full" />
            </div>
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-4">{path1.label}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-brown leading-tight">{path1.title}</h2>

              <dl className="mt-8 grid grid-cols-2 gap-5">
                <div>
                  <dt className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">Underlying framework</dt>
                  <dd className="font-serif text-xl text-brand-brown mt-2">{path1.framework}</dd>
                </div>
                <div>
                  <dt className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">Core question</dt>
                  <dd className="font-serif text-xl italic text-brand-brown-light mt-2">{path1.core_question}</dd>
                </div>
              </dl>

              <div className="mt-8">
                <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted mb-3">Themes</p>
                <div className="flex flex-wrap gap-2">
                  {path1.themes.map((t) => (
                    <span key={t} className="text-sm font-sans px-3.5 py-1.5 rounded-full bg-brand-sage text-brand-brown">{t}</span>
                  ))}
                </div>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4">
                <div className="bg-brand-bone rounded-2xl p-5">
                  <div className="font-sans text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted">Begins</div>
                  <div className="font-serif text-lg mt-1.5 text-brand-brown leading-tight">15 Sept 2026</div>
                </div>
                <div className="bg-brand-bone rounded-2xl p-5">
                  <div className="font-sans text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted">Evenings</div>
                  <div className="font-serif text-lg mt-1.5 text-brand-brown leading-tight">6:30 – 8:30 pm GMT</div>
                </div>
                <div className="bg-brand-bone rounded-2xl p-5">
                  <div className="font-sans text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted">Format</div>
                  <div className="font-serif text-lg mt-1.5 text-brand-brown leading-tight">12 weeks · Online</div>
                </div>
              </div>

              <Link
                to="/booking"
                data-testid="course-cta-apply"
                className="mt-10 inline-flex items-center font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
              >
                Apply for Path I
              </Link>
            </div>
          </div>

          {/* WEEKLY SHAPE */}
          <div className="mt-24">
            <h3 className="font-serif text-3xl md:text-4xl text-brand-brown text-center mb-12">The shape of our twelve weeks</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {WEEKLY_SHAPE.map((w, i) => (
                <div key={i} data-testid={`weekly-${i}`} className="bg-brand-bone rounded-3xl p-7 border border-brand-brown/5">
                  <div className="flex items-center gap-3 mb-3">
                    <Circle size={10} className="text-brand-gold fill-brand-gold" />
                    <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted">{w.week}</span>
                  </div>
                  <h4 className="font-serif text-2xl text-brand-brown mb-3">{w.title}</h4>
                  <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 max-w-3xl mx-auto text-center font-sans text-sm text-brand-brown-muted italic">
              Each weekly session unfolds in small intentional groups, with space for reflection, gentle practice and shared learning between meetings.
            </p>
          </div>
        </div>
      </section>

      {/* PATHS 2 & 3 EXPANDED */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 space-y-24">
          {PATHS.slice(1).map((p, idx) => {
            const img = idx === 0 ? ASSETS.goldField : ASSETS.sunThroughTrees;
            const imgAlt = idx === 0
              ? 'A golden wildflower meadow opening to warm light'
              : 'Soft sunlight filtering through trees in a quiet grove';
            const imgFirst = idx % 2 === 0;
            return (
              <div key={p.id} data-testid={`course-detail-${p.id}`} className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
                <div className={`relative ${imgFirst ? '' : 'md:order-2'}`}>
                  <div className={`absolute -inset-3 rounded-3xl ${imgFirst ? 'bg-brand-sage rotate-1' : 'bg-brand-peach/40 -rotate-1'}`} aria-hidden />
                  <img src={img} alt={imgAlt} className="relative rounded-3xl object-cover w-full aspect-[4/3]" />
                </div>
                <div className={imgFirst ? '' : 'md:order-1'}>
                  <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">{p.label}</p>
                  <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{p.title}</h3>
                  <p className="font-sans text-sm text-brand-brown-muted mt-3 italic">{p.starts}</p>
                  <p className="mt-6 font-sans text-base text-brand-brown-light/90 leading-relaxed">{p.description}</p>
                  <div className="mt-6">
                    <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted mb-3">Core question</p>
                    <p className="font-serif text-xl italic text-brand-brown-light">{p.core_question}</p>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {p.themes.map((t) => (
                      <span key={t} className="text-sm font-sans px-3.5 py-1.5 rounded-full bg-brand-sage text-brand-brown">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* INVESTMENT TEASER */}
      <section className="py-20 md:py-24 bg-brand-bg-soft">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Investment</p>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">
            A considered investment in your healing.
          </h2>
          <p className="mt-5 font-sans text-base text-brand-brown-light/90 leading-relaxed max-w-2xl mx-auto">
            The full details of what participation includes — cohort size, payment options, accessibility and the shape of the whole year — sit within Your Journey.
          </p>
          <Link
            to="/investment"
            data-testid="courses-investment-cta"
            className="mt-8 inline-flex font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
          >
            See Your Journey — investment &amp; what&rsquo;s included
          </Link>
        </div>
      </section>
    </div>
  );
}
